import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, customerReqConfig } from "../../include/api";
import { ERROR, SUCCESS } from "../../include/constant";
import { preventClick, validatePhoneNumber } from "../../include/function";
import { useNotification } from "../../Toast/ToastProvider";
import { generateToken } from "../notifications/firebase";
import Layout from "../../components/user/Layout";
import LoginSlide from "./LoginSlide";
import "../../styles/Otp.css";

const Login = ({ handleCustomer }) => {
  const toast = useNotification();
  const reqConfig = customerReqConfig();
  const signUpUrl = "/register";

  // states...
  const [email, setEmail] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [otpError, setOtpError] = useState("");
  const [postLoading, setPostLoading] = useState(false);
  const [currentView, setCurrentView] = useState("login");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const emailRef = useRef(null);

  // timer states
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const timerRef = useRef(null);

  // format mm:ss
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const startTimer = () => {
    clearInterval(timerRef.current);
    setTimeLeft(300); // reset to 5 min
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
  };

  const handleRequestOtp = () => {
    const phone = String(email).trim();
    if (!validatePhoneNumber(phone)) {
      setEmailError("Enter a valid 10-digit mobile number");
      if (emailRef.current) {
        emailRef.current.focus();
      }
      return;
    }

    setEmailError("");
    setPostLoading(true);
    api
      .post("/customer/login/?flag=sendOtp", { phoneNumber: phone })
      .then((response) => {
        if (response.status === 200) {
          setOtpInput("");
          setOtpError("");
          setCurrentView("loginOTP");
          startTimer();
          toast({
            type: SUCCESS,
            message: response.data.message || "OTP sent to your mobile number",
          });
        }
      })
      .catch((error) => {
        toast({
          type: ERROR,
          message:
            error.response?.data?.message || "Unable to send OTP. Try again",
        });
      })
      .finally(() => setPostLoading(false));
  };

  const submitLoginOtp = () => {
    const phone = String(email).trim();
    if (!otpInput || otpInput.length !== 6) {
      setOtpError("Enter the 6-digit OTP");
      return;
    }

    setOtpError("");
    setPostLoading(true);
    api
      .post("/customer/login/?flag=verifyOtp", {
        phoneNumber: phone,
        otp: otpInput,
      })
      .then((response) => {
        if (response.status === 200) {
          const user = response.data.data.user;
          const token = response.data.data.token;
          handleCustomer(user, token);
          handleStoreCmToken(user.id);
          toast({ type: SUCCESS, message: response.data.message });
        }
      })
      .catch((error) => {
        toast({
          type: ERROR,
          message: error.response?.data?.message || "Invalid OTP",
        });
      })
      .finally(() => setPostLoading(false));
  };

  const handleResendOTP = () => {
    // api call for resend here
    toast({ type: SUCCESS, message: "OTP resent successfully" });
    setOtpInput("");
    setOtpError("");
    startTimer(); // restart timer
  };

  const submitOtp = () => {
    if (otpInput === "") {
      setOtpError("OTP is required");
      return;
    }
    setPostLoading(true);
    api
      .post(`/customer/?flag=verifyOtp`, {
        phoneNumber: String(email).trim(),
        otp: Number(otpInput),
      })
      .then((response) => {
        if (response.status === 200) {
          toast({ type: SUCCESS, message: "OTP verified successfully" });
          setCurrentView("set-password");
        }
      })
      .catch((error) => {
        toast({
          type: ERROR,
          message: error.response?.data?.message || "Invalid OTP",
        });
      })
      .finally(() => setPostLoading(false));
  };

  // cleanup timer
  useEffect(() => {
    if (timeLeft === 0) {
      toast({ type: ERROR, message: "Valid time expired, try again" });
    }
  }, [timeLeft]);

  // Function to validate login input
  const validateLoginField = () => {
    if (!email.trim()) {
      setEmailError("Enter your Phone Number or Email");
      // Focus input box when error
      if (emailRef.current) {
        emailRef.current.focus();
      }
      return false;
    }
    setEmailError("");
    return true;
  };

  // Handle Request OTP click
  // const handleRequestOtp = () => {
  //     if (validateLoginField()) {
  //         setCurrentView("loginOTP");
  //     }
  // };

  // Handle Login With Password click
  const handleLoginWithPassword = () => {
    if (validateLoginField()) {
      setCurrentView("loginPassword");
    }
  };

  const handlePasswordChange = (val) => {
    setPasswordError("");
    setPassword(val);
  };

  const clearFields = () => {
    setPassword("");
    setPasswordError("");
  };

  const handleStoreCmToken = async (cid) => {
    const display = "web";
    const token = await generateToken();
    const data = { cid, token, display };
    api
      .post("/fcm/", data, reqConfig)
      .then((response) => {
        if (response.status === 200) {
          toast({ type: SUCCESS, message: response.data.message });
        }
      })
      .catch((error) => {
        error.response
          ? toast({ type: ERROR, message: error.response.data.message })
          : toast({ type: ERROR, message: error.message });
      })
      .finally(() => {
        setPostLoading(false);
      });
  };

  const handleSignIn = () => {
    let error = false;
    const data = {
      emailId: String(email).trim().toLowerCase(),
      password,
    };

    if (!data.emailId) {
      setEmailError("Enter your Phone Number or Email");
      error = true;
    }
    if (!data.password) {
      setPasswordError("Enter your password");
      error = true;
    }

    if (!error) {
      setPostLoading(true);
      api
        .post("/customer/login/", data)
        .then((response) => {
          if (response.status === 200) {
            clearFields();
            const user = response.data.data.user;
            const token = response.data.data.token;
            console.log("user data", response.data.data.user);

            handleCustomer(user, token);
            handleStoreCmToken(user.id);
            toast({ type: SUCCESS, message: response.data.message });
          }
        })
        .catch((error) => {
          error.response
            ? toast({ type: ERROR, message: error.response.data.message })
            : toast({ type: ERROR, message: error.message });
        })
        .finally(() => {
          setPostLoading(false);
        });
    }
  };

  const handlePasswordReset = () => {
    const phone = String(email).trim();
    if (!validatePhoneNumber(phone)) {
      setEmailError("Enter a valid 10-digit mobile number");
      return;
    }
    setEmailError("");
    const data = {
      phoneNumber: phone,
    };
    setPostLoading(true);
    api
      .post(`/customer/resetPassword/`, data)
      .then((response) => {
        if (response.status === 200) {
          setCurrentView("otp");
          startTimer();
          toast({
            type: SUCCESS,
            message: "OTP sent to your registered mobile number",
          });
        }
      })
      .catch((error) => {
        toast({
          type: ERROR,
          message:
            error.response?.data?.message ||
            "Unable to send OTP. Please check your registered mobile number",
        });
      })
      .finally(() => setPostLoading(false));
  };

  // const submitOtp = () => {
  //     if (otpInput === '') {
  //         setOtpError('OTP is required');
  //         return;
  //     }
  //     setPostLoading(true);
  //     api.post(`/customer/?flag=verifyOtp`, { email, otp: Number(otpInput) })
  //         .then(response => {
  //             if (response.status === 200) {
  //                 toast({ type: SUCCESS, message: 'OTP verified successfully' });
  //                 setCurrentView('set-password');
  //             }
  //         })
  //         .catch(error => {
  //             toast({ type: ERROR, message: error.response?.data?.message || 'Invalid OTP' });
  //         })
  //         .finally(() => setPostLoading(false));
  // };

  const submitNewPassword = () => {
    if (newPassword === "" || confirmPassword === "") {
      setNewPasswordError("Both fields are required");
      return;
    }
    if (newPassword !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return;
    }
    const payload = {
      phoneNumber: String(email).trim(),
      otp: otpInput,
      password: newPassword,
    };
    setPostLoading(true);
    api
      .put(`/customer/resetPassword/?flag=updatePassword`, payload)
      .then((response) => {
        if (response.status === 200) {
          toast({ type: SUCCESS, message: "Password updated successfully" });
          setCurrentView("loginPassword");
        }
      })
      .catch((error) => {
        toast({
          type: ERROR,
          message: error.response?.data?.message || "Something went wrong",
        });
      })
      .finally(() => setPostLoading(false));
  };

  const getPasswordStrength = (password) => {
    if (password.length === 0) return "";
    if (password.length < 6) return "Weak";
    if (password.length < 10) return "Moderate";
    if (
      /[A-Z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[^A-Za-z0-9]/.test(password)
    ) {
      return "Strong";
    }
    return "Moderate";
  };
  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  return (
    <Layout>
      <br />
      <section className="account pb-40">
        <div className="container min-h-screen align-items-center justify-content-center">
          <div className="border border-gray-100 hover-border-main-600 transition-1 rounded-16">
            {currentView === "login" && (
              <div
                className="container-fluid p-0"
                style={{ overflow: "hidden" }}
              >
                <div className="row g-0 align-items-center">
                  {/* Left Side - Slideshow Section */}
                  <div className="col-md-6">
                    {" "}
                    <LoginSlide />
                  </div>

                  {/* Right Side - Form Section */}
                  <div className="col-md-6 px-24 py-40">
                    <form action="#">
                      <h6 className="text-xl mb-32 fw-bold">LOGIN</h6>

                      <div className="mb-24">
                        <label className="text-dark mb-8 fw-medium fw-bold">
                          Enter registered Phone Number or Email
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          ref={emailRef}
                          type="email"
                          className={`common-input form-control ${
                            emailError ? "border-danger" : ""
                          }`}
                          placeholder="Phone number or Email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                        <div className="text-danger small">{emailError}</div>
                        <br />
                        <div>
                          By continuing, you agree to Buildton's{" "}
                          <a className="text-decoration-none">Terms of Use</a>{" "}
                          and{" "}
                          <a className="text-decoration-none">Privacy Policy</a>
                        </div>
                      </div>

                      <div className="mb-24 mt-48">
                        <div className="flex-align gap-5 flex-wrap">
                          <button
                            type="submit"
                            className="btn btn-main py-18 px-40 me-5"
                            onClick={(e) => preventClick(e, handleRequestOtp)}
                            disabled={postLoading}
                          >
                            {postLoading ? "Please wait..." : "REQUEST OTP"}
                          </button>
                          <div className="text-xl text-dark">or</div>
                          <button
                            type="submit"
                            className="btn btn-main py-18 px-40 ms-5"
                            onClick={(e) =>
                              preventClick(e, handleLoginWithPassword)
                            }
                            disabled={postLoading}
                          >
                            {postLoading
                              ? "Please wait..."
                              : "LOGIN WITH PASSWORD"}
                          </button>
                        </div>
                      </div>

                      <div className="mt-48">
                        <div className="mt-32">
                          {signUpUrl && (
                            <Link
                              to={signUpUrl}
                              className="text-success fw-semibold hover-text-decoration-underline"
                            >
                              Not yet a customer? Create an account
                            </Link>
                          )}
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
            {currentView === "loginOTP" && (
              <div
                className="container-fluid p-0"
                style={{ overflow: "hidden" }}
              >
                <div className="row g-0 align-items-center">
                  <div className="col-md-6">
                    <LoginSlide />
                  </div>
                  <div className="col-md-6 px-24 py-40">
                    <form action="#">
                      <h6 className="text-xl mb-32 fw-bold">LOGIN</h6>

                      <div className="mb-24">
                        <div className="d-flex justify-content-between">
                          <label className="text-dark mb-8 fw-medium fw-bold">
                            Please enter the OTP sent to +91{email}
                            <span className="text-danger">*</span>
                          </label>
                          <span>
                            <a
                              className="text-decoration-none cursor-pointer"
                              onClick={() => setCurrentView("login")}
                            >
                              Change
                            </a>
                          </span>
                        </div>

                        <div className="form-group mb-4">
                          <OtpInput
                            length={6}
                            onChange={(val) => setOtpInput(val)}
                          />
                          {<div className="text-danger small">{otpError}</div>}
                        </div>

                        {/* Timer / Back Button */}
                        {timeLeft > 0 && (
                          <div>
                            Not received your code?{" "}
                            <b>{formatTime(timeLeft)}</b>
                          </div>
                        )}
                      </div>

                      <div className="mb-24 mt-48">
                        <div className="mb-24 mt-48">
                          {timeLeft > 0 ? (
                            <button
                              type="submit"
                              className="btn btn-main py-18 px-40"
                              onClick={(e) => preventClick(e, submitLoginOtp)}
                              disabled={postLoading}
                            >
                              {postLoading ? "Please wait..." : "VERIFY OTP"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-main py-18 px-40"
                              onClick={() => setCurrentView("login")}
                            >
                              BACK TO LOGIN
                            </button>
                          )}
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {currentView === "loginPassword" && (
              <div
                className="container-fluid p-0"
                style={{ overflow: "hidden" }}
              >
                <div className="row g-0 align-items-center">
                  {/* Left Side - Slideshow Section */}
                  <div className="col-md-6">
                    <LoginSlide />
                  </div>

                  {/* Right Side - Form Section */}
                  <div className="col-md-6 px-24 py-40">
                    <form action="#">
                      <h6 className="text-xl mb-32 fw-bold">LOGIN</h6>

                      <div className="mb-24">
                        <label className="text-dark mb-8 fw-medium fw-bold">
                          Please enter the password
                          <span className="text-danger">*</span>
                        </label>
                        <div className="position-relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            className={`common-input form-control ${
                              passwordError ? "border-danger" : ""
                            }`}
                            placeholder="Enter Password"
                            value={password}
                            onChange={(e) =>
                              handlePasswordChange(e.target.value)
                            }
                          />

                          <i
                            className={`toggle-password position-absolute top-50 translate-middle-y inset-inline-end-0 me-16 cursor-pointer ph ${
                              showPassword ? "ph-eye" : "ph-eye-slash"
                            }`}
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ fontSize: "18px" }}
                          />
                        </div>
                        <div className="text-danger small">{passwordError}</div>
                      </div>

                      <div className="mb-24 mt-48">
                        <div className="flex-align gap-5 flex-wrap">
                          <button
                            type="submit"
                            className="btn btn-main py-18 px-40"
                            onClick={(e) => preventClick(e, handleSignIn)}
                            disabled={postLoading}
                          >
                            {postLoading ? "Please wait..." : "LOGIN"}
                          </button>
                        </div>
                      </div>

                      <div className="mt-48">
                        <div className="mt-32">
                          <div className="text-start">
                            <Link
                              to="#"
                              onClick={() => setCurrentView("forgot")}
                              className="text-danger text-sm fw-semibold hover-text-decoration-underline"
                            >
                              Forgot your Password?
                            </Link>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
            {currentView === "forgot" && (
              <div
                className="container-fluid p-0"
                style={{ overflow: "hidden" }}
              >
                <div className="row g-0 align-items-center">
                  {/* Left Side - Slideshow Section */}
                  <div className="col-md-6">
                    {" "}
                    <LoginSlide />
                  </div>

                  {/* Right Side - Form Section */}
                  <div className="col-md-6 px-24 py-40">
                    <form action="#">
                      <h6 className="text-xl mb-32 fw-bold">FORGOT PASSWORD</h6>

                      <div className="mb-24">
                        <label className="text-dark mb-8 fw-medium fw-bold">
                          Registered Mobile Number
                          <span className="text-danger">*</span>
                        </label>
                        <div className="position-relative">
                          <input
                            type="tel"
                            inputMode="numeric"
                            maxLength={10}
                            className={`common-input form-control ${
                              emailError ? "border-danger" : ""
                            }`}
                            placeholder="Enter registered mobile number"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                          <div className="text-danger small">{emailError}</div>
                        </div>
                      </div>
                      <div className="mb-24 mt-48">
                        <div className="flex-align gap-5 flex-wrap">
                          <button
                            className="btn btn-main py-18 px-40"
                            onClick={(e) =>
                              preventClick(e, handlePasswordReset)
                            }
                            disabled={postLoading}
                          >
                            {postLoading ? "Sending OTP..." : "SEND OTP"}
                          </button>
                        </div>
                      </div>

                      <div className="mt-48">
                        <div className="mt-32">
                          <Link
                            onClick={() => setCurrentView("login")}
                            className="text-success fw-semibold hover-text-decoration-underline"
                          >
                            Back to Login
                          </Link>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
            {currentView === "otp" && (
              <div
                className="container-fluid p-0"
                style={{ overflow: "hidden" }}
              >
                <div className="row g-0 align-items-center">
                  {/* Left Side - Slideshow Section */}
                  <div className="col-md-6">
                    <LoginSlide />
                  </div>

                  {/* Right Side - Form Section */}
                  <div className="col-md-6 px-24 py-40">
                    <form action="#">
                      <h6 className="text-xl mb-32 fw-bold">ENTER OTP</h6>

                      <div className="form-group mb-24">
                        <label className="text-dark mb-8 fw-medium fw-bold">
                          Please Enter the OTP
                          <span className="text-danger">*</span>
                        </label>
                        <OtpInput
                          length={6}
                          onChange={(val) => setOtpInput(val)}
                        />
                        <div className="text-danger small">{otpError}</div>

                        {timeLeft > 0 && (
                          <div>
                            Not received your code?{" "}
                            <b>{formatTime(timeLeft)}</b>
                          </div>
                        )}
                      </div>
                      <div className="mb-24 mt-48">
                        <div className="flex-align gap-5 flex-wrap">
                          {timeLeft > 0 ? (
                            <button
                              type="submit"
                              className="btn btn-main py-18 px-40"
                              onClick={(e) => preventClick(e, submitOtp)} // ✅ use preventClick
                              disabled={postLoading}
                            >
                              {postLoading ? "Please wait..." : "VERIFY OTP"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-main py-18 px-40"
                              onClick={() => setCurrentView("forgot")}
                            >
                              GO BACK
                            </button>
                          )}
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
            {currentView === "set-password" && (
              <div
                className="container-fluid p-0"
                style={{ overflow: "hidden" }}
              >
                <div className="row g-0 align-items-center">
                  {/* Left Side - Slideshow Section */}
                  <div className="col-md-6">
                    {" "}
                    <LoginSlide />
                  </div>

                  {/* Right Side - Form Section */}
                  <div className="col-md-6 px-24 py-40">
                    <form action="#">
                      <h6 className="text-xl mb-32 fw-bold">
                        SET NEW PASSWORD
                      </h6>

                      <div className="form-group mb-24">
                        <label className="text-dark mb-8 fw-medium fw-bold">
                          New Password
                          <span className="text-danger">*</span>
                        </label>
                        <div className="position-relative">
                          <input
                            type={showNewPassword ? "text" : "password"}
                            className={`common-input form-control ${
                              newPasswordError ? "border-danger" : ""
                            }`}
                            placeholder="Enter new password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                          />
                          <i
                            className={`toggle-password position-absolute top-50 translate-middle-y inset-inline-end-0 me-16 cursor-pointer ph ${
                              showNewPassword ? "ph-eye" : "ph-eye-slash"
                            }`}
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            style={{ fontSize: "18px" }}
                          />
                        </div>
                        {/* Password messages */}
                        <div
                          className="d-flex justify-content-between mt-1"
                          style={{ fontSize: "14px" }}
                        >
                          <span style={{ fontWeight: "600" }}>
                            <a className="text-decoration-none">
                              {" "}
                              Atleast 8 characters
                            </a>
                          </span>

                          <span
                            style={{
                              color:
                                getPasswordStrength(newPassword) === "Strong"
                                  ? "green"
                                  : getPasswordStrength(newPassword) ===
                                    "Moderate"
                                  ? "orange"
                                  : "red",
                              fontWeight: "600",
                            }}
                          >
                            {getPasswordStrength(newPassword)}
                          </span>
                        </div>
                        <div className="text-danger small">
                          {newPasswordError}
                        </div>
                      </div>
                      <div className="form-group mb-24">
                        <label className="text-dark mb-8 fw-medium fw-bold">
                          Confirm Password
                          <span className="text-danger">*</span>
                        </label>
                        <div className="position-relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            className={`common-input form-control ${
                              confirmPasswordError ? "border-danger" : ""
                            }`}
                            placeholder="Confirm password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                          />
                          <i
                            className={`toggle-password position-absolute top-50 translate-middle-y inset-inline-end-0 me-16 cursor-pointer ph ${
                              showConfirmPassword ? "ph-eye" : "ph-eye-slash"
                            }`}
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            style={{ fontSize: "18px" }}
                          />
                        </div>
                        <div className="text-danger small">
                          {confirmPasswordError}
                        </div>
                      </div>
                      <div className="mb-24 mt-48">
                        <div className="flex-align gap-5 flex-wrap">
                          <button
                            className="btn btn-main py-18 px-40"
                            onClick={(e) => preventClick(e, submitNewPassword)}
                            disabled={postLoading}
                          >
                            {postLoading ? "LOADING..." : "SUBMIT"}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Login;

const OtpInput = ({ length = 6, onChange }) => {
  const inputs = useRef([]);

  const handleChange = (e, index) => {
    const value = e.target.value.replace(/[^0-9]/g, ""); // only numbers
    if (!value) return;

    // update parent
    let otp = inputs.current.map((input) => input.value).join("");
    onChange(otp);

    // move focus next
    if (index < length - 1 && value) {
      inputs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      inputs.current[index - 1].focus();
    }
  };

  return (
    <div className="d-flex gap-2">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          type="text"
          inputMode="numeric" // mobile keypad la numbers varum
          pattern="[0-9]*" // only numbers allowed
          maxLength={1}
          ref={(el) => (inputs.current[i] = el)}
          className="otp-box"
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onInput={(e) => {
            e.target.value = e.target.value.replace(/[^0-9]/g, ""); // remove non-numeric
          }}
        />
      ))}
    </div>
  );
};
